import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/index.js';
import { createQrDetector, firstSessionCode } from '../lib/qrScan.js';

interface QrScannerProps {
  onCode: (code: string) => void;
  onClose: () => void;
}

type ScannerStatus = 'starting' | 'scanning' | 'denied' | 'unavailable';

/** How often a frame is examined. Four times a second is well past the rate
 *  somebody can aim a phone, and leaves the CPU alone between looks. */
const INTERVAL_MS = 250;

/**
 * The camera view that reads an invite code off another phone's screen.
 *
 * The browser's own barcode reader does the decoding — see `qrScan.ts` for
 * why this is not offered where that is missing. What is left here is the
 * part that is easy to get wrong: a camera stream must be stopped on every
 * exit, including the ones that are not a click on "close". A page that
 * leaves the camera light on is the kind of thing people uninstall an app
 * over, so the release happens in the effect's own cleanup, which React runs
 * on unmount whatever the reason.
 */
export function QrScanner({ onCode, onClose }: QrScannerProps) {
  const t = useT();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<ScannerStatus>('starting');
  // Kept in a ref as well: the interval closure must see the latest callback
  // without restarting the camera every time the parent re-renders.
  const onCodeRef = useRef(onCode);
  useEffect(() => {
    onCodeRef.current = onCode;
  }, [onCode]);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let cancelled = false;

    const stop = () => {
      if (timer !== undefined) window.clearInterval(timer);
      for (const track of stream?.getTracks() ?? []) track.stop();
      stream = null;
    };

    const start = async () => {
      const detector = await createQrDetector();
      if (cancelled) return;
      if (!detector) {
        setStatus('unavailable');
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // The back camera, which is the one pointing at the other phone.
          video: { facingMode: 'environment' },
          audio: false,
        });
      } catch {
        // Refused, already in use, or no camera at all — all the same answer
        // as far as anybody standing here is concerned.
        if (!cancelled) setStatus('denied');
        return;
      }

      if (cancelled || !videoRef.current) {
        stop();
        return;
      }

      const video = videoRef.current;
      video.srcObject = stream;
      try {
        // Autoplay can be refused, and older Safari returns nothing at all
        // from `play()` rather than a promise — hence await-in-try rather
        // than a `.catch` on the result.
        await video.play();
      } catch {
        // The frame stays black until the first decoded image; the detector
        // reads the element either way once it has data.
      }
      if (cancelled) {
        stop();
        return;
      }
      setStatus('scanning');

      timer = window.setInterval(() => {
        void (async () => {
          if (!videoRef.current) return;
          try {
            const results = await detector.detect(videoRef.current);
            const code = firstSessionCode(results);
            if (code) onCodeRef.current(code);
          } catch {
            // A frame that cannot be read is the ordinary case while
            // somebody is still aiming. Keep looking.
          }
        })();
      }, INTERVAL_MS);
    };

    void start();

    return () => {
      cancelled = true;
      stop();
    };
  }, []);

  return (
    <div className="qr-scanner">
      <div className="qr-scanner__frame">
        <video ref={videoRef} className="qr-scanner__video" muted playsInline />
        <span className="qr-scanner__reticle" aria-hidden="true" />
      </div>
      <p className="muted small center">
        {status === 'scanning'
          ? t('join.scanHint')
          : status === 'denied'
            ? t('join.scanDenied')
            : status === 'unavailable'
              ? t('join.scanUnavailable')
              : t('common.loading')}
      </p>
      <button type="button" className="btn btn--ghost btn--block" onClick={onClose}>
        {t('common.close')}
      </button>
    </div>
  );
}
